import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslations } from '@/i18n/translations';
import { Eyebrow } from '@leanin/ui';
import { Body, Heading } from '@leanin/ui/server';
import { isUuid } from '@leanin/shared';
import { ContestSubmitForm } from '@/components/moderation/ContestSubmitForm';
import { ErrorState } from '@leanin/ui';
import styles from './page.module.css';

type TargetKind = 'post' | 'comment' | 'message' | 'event' | 'resource' | 'member';

const TARGET_KINDS: ReadonlyArray<TargetKind> = [
  'post',
  'comment',
  'message',
  'event',
  'resource',
  'member',
];

const isTargetKind = (value: string): value is TargetKind =>
  (TARGET_KINDS as ReadonlyArray<string>).includes(value);

type Search = {
  targetKind?: TargetKind;
  targetId?: string;
  holdId?: string;
};

function pickStr(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

export const Route = createFileRoute('/_authed/moderation/contest/')({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const kind =
      typeof raw.targetKind === 'string' && isTargetKind(raw.targetKind)
        ? (raw.targetKind as TargetKind)
        : undefined;
    return {
      ...(kind !== undefined ? { targetKind: kind } : {}),
      ...(pickStr(raw.targetId) ? { targetId: pickStr(raw.targetId) as string } : {}),
      ...(pickStr(raw.holdId) ? { holdId: pickStr(raw.holdId) as string } : {}),
    };
  },
  component: ContestSubmitPage,
});

function ContestSubmitPage() {
  const t = useTranslations('Moderation');
  const params = Route.useSearch();
  const targetKind = params.targetKind ?? null;
  const targetId =
    params.targetId !== undefined && isUuid(params.targetId)
      ? params.targetId
      : null;

  if (targetKind === null || targetId === null) {
    return (
      <ErrorState
        title={t('contest.missingContextTitle')}
        body={t('contest.missingContextBody')}
        reference="moderation/contest_missing_context"
      />
    );
  }

  return (
    <div className={styles.page}>
      <Link
        to={
          params.holdId
            ? '/moderation/auto-hide-notice/$holdId'
            : '/feed'
        }
        {...(params.holdId ? { params: { holdId: params.holdId } } : {})}
        className={styles.back}
      >
        {t('contest.back')}
      </Link>
      <div className={styles.eyebrowSpacer}>
        <Eyebrow>{t('contest.eyebrow')}</Eyebrow>
      </div>
      <Heading level={1} className={styles.heading}>{t('contest.heading')}</Heading>
      <Body className={styles.lede}>{t('contest.lede')}</Body>

      <div className={styles.contextCard}>
        <div className={styles.contextLabel}>{t('contest.contextLabel')}</div>
        <div className={styles.contextValue}>
          {targetKind} · {targetId.slice(0, 8)}
        </div>
        <div className={styles.contextSecondary}>{t('contest.contextSecondary')}</div>
      </div>

      <ContestSubmitForm
        targetKind={targetKind}
        targetId={targetId}
        holdId={params.holdId}
      />
    </div>
  );
}
